// https://li.feishu.cn/docs/doccn93NBILGnyKGGIomGXewLCd
import { registerPlugin } from "@capacitor/core";
import { isNative } from ".";

export interface CarPropertyValue {
  propertyId: number;
  propertyName: string;
  value: string;
  timestamp: number; // 时间戳，单位：毫秒（Unix 时间戳，Java 端已转换）
  onChangeFlag?: number; // 0：UNDEFINED 1：VALUE_CHANGED 2：VALUE_UNCHANGED
  status: number;
  areaId: number;
  type?: string;
}

export interface VehicleInfoValue {
  infoId: string;
  value: string;
}

export interface CarPlugin {
  // 连接状态
  isConnected(): Promise<{ connected: boolean }>;
  disconnect(): Promise<void>;

  // 获取属性
  getProperty(options: {
    propertyName: string;
    areaId?: number;
  }): Promise<CarPropertyValue>;

  getProperties(options: {
    propertyNames: string[];
    areaId?: number;
  }): Promise<{ properties: CarPropertyValue[] }>;

  // 获取属性配置信息（包括访问模式）
  getPropertyConfig(options: {
    propertyName: string;
  }): Promise<{
    propertyId: number;
    propertyName: string;
    access: number; // 0 = NONE, 1 = READ, 2 = WRITE, 3 = READ_WRITE
    isReadable: boolean;
    isWritable: boolean;
    changeMode: number; // 0 = STATIC, 1 = ON_CHANGE, 2 = CONTINUOUS
    type?: string; // 属性类型，如 Integer, Float, Boolean 等
    supportedAreas?: Array<{
      areaId: number;
      minValue: any;
      maxValue: any;
    }>;
    minSampleRate: number;
    maxSampleRate: number;
  }>;

  // 设置属性
  setProperty(options: {
    propertyName: string;
    areaId?: number;
    value: number | boolean | string;
    valueType?: "int" | "float" | "boolean";
  }): Promise<void>;

  // 订阅属性
  subscribeProperty(options: {
    propertyName: string;
    areaId?: number;
  }): Promise<{
    propertyName: string;
    eventName: string;
    errorEventName: string;
  }>;

  // 取消订阅
  unsubscribeProperty(options: { propertyName: string }): Promise<void>;

  // 通过诊断管理器获取车辆信息
  getVehicleInfo(options: {
    infoId: string;
  }): Promise<{ infoId: string; value: string }>;

  // 批量获取车辆信息
  getVehicleInfos(options: {
    infoIds: string[];
  }): Promise<{ infos: VehicleInfoValue[] }>;

  // 获取所有 VehicleProperty 属性
  getAllVehicleProperties(options?: {
    areaId?: number;
    includeValues?: boolean;
  }): Promise<{
    properties: Array<{
      propertyName: string;
      propertyId: number;
      value?: any;
      status?: number;
      timestamp?: number;
      areaId?: number;
    }>;
    count: number;
  }>;

  // 监听事件
  addListener(
    eventName:
      | keyof typeof VehiclePropertyNames
      | "propertyChange"
      | "propertyError",
    listenerFunc: (data: CarPropertyValue) => void
  ): Promise<{ remove: () => void }>;

  removeAllListeners(): Promise<void>;
}

// 常用车辆属性名称常量，用于提供代码提示
export const VehiclePropertyNames = {
  // 车速
  VehDisplaySpd: "VehDisplaySpd",
  VehSpd: "VehSpd",
  // 逻辑档位信号
  ActuGearShiftPos: "ActuGearShiftPos",
  // 前排空调开关
  HuFrtACSwReq: "HuFrtACSwReq",
  // 矩阵版本
  MATRIX_SOA_VERSION: "MATRIX_SOA_VERSION",
  MATRIX_ECAN_VERSION: "MATRIX_ECAN_VERSION",
  MATRIX_ICAN_VERSION: "MATRIX_ICAN_VERSION",
  MATRIX_MCU_VERSION: "MATRIX_MCU_VERSION",
  // 经纬度 - INS相关
  INS_MSG_InsLatitude: "INS_MSG_InsLatitude",
  INS_MSG_InsLongitude: "INS_MSG_InsLongitude",
  INS_Latitude: "INS_Latitude",
  INS_Longitude: "INS_Longitude",
  INS_Latitude_AD4: "INS_Latitude_AD4",
  INS_Longitude_AD4: "INS_Longitude_AD4",
  // 经纬度 - GNSS相关
  GNSSLOC_Latitude: "GNSSLOC_Latitude",
  GNSSLOC_Longitude: "GNSSLOC_Longitude",
  GNSSLOC_Latitude_AD4: "GNSSLOC_Latitude_AD4",
  GNSSLOC_Longitude_AD4: "GNSSLOC_Longitude_AD4",
  GNSSLOC_MSG_GnssLocLatitude: "GNSSLOC_MSG_GnssLocLatitude",
  GNSSLOC_MSG_GnssLocLongitude: "GNSSLOC_MSG_GnssLocLongitude",
  // VIN码
  INFO_VIN: "INFO_VIN",
  // HDC激活状态
  HDCCtrlA: "HDCCtrlA",
  // 方向盘转向角速度
  EPS_StrgAngSpeed: "EPS_StrgAngSpeed",
  // 方向盘转角
  EPS_StrgAng: "EPS_StrgAng",
  // 魔毯风格设置请求
  AirSusAdjust_SRV_AirSusStyleSwReq: "AirSusAdjust_SRV_AirSusStyleSwReq",
  // 前方道路特征
  FrontRoadFeature: "FrontRoadFeature",
  FrontRoadFeatureDistance: "FrontRoadFeatureDistance",
  FrontRoadFeatureLevel: "FrontRoadFeatureLevel",
  FrontRoadFeatureLength: "FrontRoadFeatureLength",
  FrontRoadFeatureLevelMax: "FrontRoadFeatureLevelMax",
  FrontRoadFeatureLevelMin: "FrontRoadFeatureLevelMin",
  FrontRoadFeatureSts: "FrontRoadFeatureSts",
  // 悬架相关
  SusComfortLevelDisplay: "SusComfortLevelDisplay",
  SusDamperLevelDisplay: "SusDamperLevelDisplay",
  SusIMUAccVert: "SusIMUAccVert",
  SusWhlAccFL: "SusWhlAccFL",
  SusWhlAccFR: "SusWhlAccFR",
  SusActuHghtLvl: "SusActuHghtLvl",
  SusActuChmbMd: "SusActuChmbMd",
  // INS航向角
  INS_HeadingAngle: "INS_HeadingAngle",
} as const;

// 诊断管理器获取的车辆信息ID常量
export const VehicleInfoIds = {
  // 车辆识别号
  VEHICLE_VIN_ID: "vin_code",
  // ECU流水编号
  VEHICLE_SN_ID: "sn_code",
  // 整车固件软件版本号
  VEHICLE_VFS_ID: "vfs_code",
  // 车辆制造商零部件号
  VEHICLE_VMSPN_ID: "vmspn_code",
  // 供应商标识
  VEHICLE_SSID_ID: "ssid_code",
  // ECU名称
  VEHICLE_ECU_NAME_ID: "ecu_name",
  // ECU硬件版本号
  VEHICLE_ECU_HW_ID: "ecu_hw_code",
  // ECU软件版本号
  VEHICLE_ECU_SW_ID: "ecu_sw_code",
  // 车辆制造日期
  VEHICLE_VM_DATE_ID: "vm_date",
  // ECU制造日期
  VEHICLE_ECU_DATE_ID: "ecu_date",
  // 指纹信息
  VEHICLE_FP_ID: "finger_print",
  // 12NC码
  VEHICLE_12NC_ID: "12nc_code",
  // ECU功能配置字
  VEHICLE_ECU_FUNC_CONFIG_ID: "ecu_func_config",
  // TRACE_ID
  VEHICLE_TRACE_ID_ID: "trace_id",
  // EOL整车配置字
  VEHICLE_EOL_VEHICLE_CONFIG_ID: "eol_vehicle_config",
  // 引导程序版本号
  VEHICLE_BOOT_SW_VERSION_ID: "boot_sw_id",
  // 车辆标识 for open SDK
  VEHICLE_LDID_ID: "ldid_id",
  // WIFI名称/密码
  VEHICLE_EOL_WIFI_SSID_PWD_ID: "eol_wifi_ssid_pwd",
  // Modem Secure boot 状态
  VEHICLE_MODEM_SECURE_BOOT_STATUS_ID: "modem_secure_boot_status",
  // 车型配置
  VEHICLE_VC_MODELS: "vc_models",
  // 销售区域
  VEHICLE_VC_SALES_AREA: "sales_area",
} as const;

// 导出属性名称类型，用于类型检查
export type VehiclePropertyName = keyof typeof VehiclePropertyNames;
export type VehicleInfoId = string;

const Car = registerPlugin<CarPlugin>("Car");

export const getVIN = async () => {
  const info = await Car.getVehicleInfo({
    infoId: VehicleInfoIds.VEHICLE_VIN_ID,
  });
  return info.value;
};

// 创建 CarClient 类来管理属性订阅，类似于 BleClient
class CarClientClass {
  private eventListeners = new Map<
    string,
    { remove: () => Promise<void> | void }
  >();

  /**
   * 订阅车辆属性变化通知
   * @param propertyName 属性名称
   * @param areaId 区域ID，可选
   * @param callback 回调函数，当属性值变化时调用
   */
  async subscribeProperty(
    propertyName: string,
    areaId: number = 0,
    callback: (value: CarPropertyValue) => void
  ): Promise<void> {
    // 使用 propertyName 作为 key
    const key = propertyName;

    // 移除已存在的监听器
    const existingListener = this.eventListeners.get(key);
    if (existingListener) {
      await existingListener.remove();
    }

    // 添加新的监听器
    const listener = await Car.addListener(propertyName as any, callback);
    this.eventListeners.set(key, listener);

    // 调用原生方法订阅属性
    await Car.subscribeProperty({
      propertyName,
      areaId,
    });
  }

  /**
   * 取消订阅车辆属性变化通知
   * @param propertyName 属性名称
   */
  async unsubscribeProperty(propertyName: string): Promise<void> {
    const key = propertyName;

    // 移除事件监听器
    const listener = this.eventListeners.get(key);
    if (listener) {
      await listener.remove();
      this.eventListeners.delete(key);
    }

    // 调用原生方法取消订阅属性
    await Car.unsubscribeProperty({
      propertyName,
    });
  }

  /**
   * 移除所有监听器
   */
  async removeAllListeners(): Promise<void> {
    // 移除所有事件监听器
    for (const [, listener] of this.eventListeners) {
      await listener.remove();
    }
    this.eventListeners.clear();

    // 调用原生方法移除所有监听器
    await Car.removeAllListeners();
  }

  // 代理其他 Car 方法，保持接口一致性
  async isConnected() {
    return Car.isConnected();
  }

  async disconnect() {
    return Car.disconnect();
  }

  async getProperty(options: { propertyName: string; areaId?: number }) {
    return Car.getProperty(options);
  }

  async getProperties(options: { propertyNames: string[]; areaId?: number }) {
    return Car.getProperties(options);
  }

  async getPropertyConfig(options: { propertyName: string }) {
    return Car.getPropertyConfig(options);
  }

  async setProperty(options: {
    propertyName: string;
    areaId?: number;
    value: number | boolean | string;
    valueType?: "int" | "float" | "boolean";
  }) {
    return Car.setProperty(options);
  }

  async getVehicleInfo(options: { infoId: string }) {
    return Car.getVehicleInfo(options);
  }

  async getVehicleInfos(options: { infoIds: string[] }) {
    return Car.getVehicleInfos(options);
  }

  async getAllVehicleProperties(options?: { areaId?: number; includeValues?: boolean }) {
    return Car.getAllVehicleProperties(options || {});
  }

  async addListener(
    eventName: keyof typeof VehiclePropertyNames,
    listenerFunc: (data: CarPropertyValue) => void
  ) {
    return Car.addListener(eventName, listenerFunc);
  }

  async getVIN() {
    return getVIN();
  }
  // 获取矩阵版本
  async getMatrixVersions(areaId: number = 0) {
    const propertyNames = [
      VehiclePropertyNames.MATRIX_SOA_VERSION,
      VehiclePropertyNames.MATRIX_ECAN_VERSION,
      VehiclePropertyNames.MATRIX_ICAN_VERSION,
      VehiclePropertyNames.MATRIX_MCU_VERSION,
    ];

    try {
      const { properties } = await this.getProperties({
        propertyNames,
        areaId,
      });
      const versions: {
        soaVersion?: string;
        ecanVersion?: string;
        icanVersion?: string;
        mcuVersion?: string;
      } = {};

      for (const property of properties) {
        switch (property.propertyName) {
          case VehiclePropertyNames.MATRIX_SOA_VERSION:
            versions.soaVersion = property.value;
            break;
          case VehiclePropertyNames.MATRIX_ECAN_VERSION:
            versions.ecanVersion = property.value;
            break;
          case VehiclePropertyNames.MATRIX_ICAN_VERSION:
            versions.icanVersion = property.value;
            break;
          case VehiclePropertyNames.MATRIX_MCU_VERSION:
            versions.mcuVersion = property.value;
            break;
          default:
            break;
        }
      }
      return versions;
    } catch (error) {
      console.error("[CarClient] Failed to get matrix versions", error);
      throw error;
    }
  }
}

/**
 * 格式化时间戳为可读的日期时间字符串
 * @param timestamp 时间戳（毫秒，Unix 时间戳）
 * @returns 格式化的日期时间字符串
 */
export const formatTimestamp = (timestamp: number): string => {
  return new Date(timestamp).toLocaleString();
};


/**
 * 翻译访问模式枚举值
 * @param access 访问模式数值 (0 = NONE, 1 = READ, 2 = WRITE, 3 = READ_WRITE)
 * @returns 中文（枚举英文）格式
 */
export const translateAccessMode = (access: number): string => {
  switch (access) {
    case 0:
      return "无权限（NONE）";
    case 1:
      return "只读（READ）";
    case 2:
      return "只写（WRITE）";
    case 3:
      return "读写（READ_WRITE）";
    default:
      return `未知（${access}）`;
  }
};

/**
 * 翻译访问模式字符串（兼容旧版本）
 * @param accessMode 访问模式字符串或数值
 * @returns 中文（枚举英文）格式
 */
export const translateAccessModeString = (accessMode: string | number): string => {
  if (typeof accessMode === "number") {
    return translateAccessMode(accessMode);
  }

  switch (accessMode) {
    case "NONE":
      return "无权限（NONE）";
    case "READ":
    case "READ_ONLY":
      return "只读（READ）";
    case "WRITE":
    case "WRITE_ONLY":
      return "只写（WRITE）";
    case "READ_WRITE":
      return "读写（READ_WRITE）";
    default:
      return `未知（${accessMode}）`;
  }
};

/**
 * 翻译变化模式枚举值
 * @param changeMode 变化模式数值 (0 = STATIC, 1 = ON_CHANGE, 2 = CONTINUOUS)
 * @returns 中文（枚举英文）格式
 */
export const translateChangeMode = (changeMode: number): string => {
  switch (changeMode) {
    case 0:
      return "静态（STATIC）";
    case 1:
      return "变化上报（ON_CHANGE）";
    case 2:
      return "周期上报（CONTINUOUS）";
    default:
      return `未知（${changeMode}）`;
  }
};

/**
 * 翻译变化模式字符串（兼容旧版本）
 * @param changeMode 变化模式字符串或数值
 * @returns 中文（枚举英文）格式
 */
export const translateChangeModeString = (changeMode: string | number): string => {
  if (typeof changeMode === "number") {
    return translateChangeMode(changeMode);
  }

  switch (changeMode) {
    case "STATIC":
      return "静态（STATIC）";
    case "ON_CHANGE":
      return "变化上报（ON_CHANGE）";
    case "CONTINUOUS":
      return "周期上报（CONTINUOUS）";
    default:
      return `未知（${changeMode}）`;
  }
};

/**
 * 翻译状态枚举值
 * @param status 状态数值 (0 = AVAILABLE, 1 = UNAVAILABLE, 2 = ERROR)
 * @returns 中文（枚举英文）格式
 */
export const translateStatus = (status: number | string): string => {
  const statusNum = typeof status === "string" ? parseInt(status) : status;
  switch (statusNum) {
    case 0:
      return "可用（AVAILABLE）";
    case 1:
      return "不可用（UNAVAILABLE）";
    case 2:
      return "错误（ERROR）";
    default:
      if (typeof status === "string") {
        // 处理字符串状态
        switch (status) {
          case "OK":
            return "正常（OK）";
          case "TRY_AGAIN":
            return "重试（TRY_AGAIN）";
          default:
            return `${status}（${status}）`;
        }
      }
      return `未知（${status}）`;
  }
};

/**
 * 翻译变化标志枚举值
 * @param onChangeFlag 变化标志数值 (0 = UNDEFINED, 1 = VALUE_CHANGED, 2 = VALUE_UNCHANGED)
 * @returns 中文（枚举英文）格式
 */
export const translateOnChangeFlag = (onChangeFlag: number): string => {
  switch (onChangeFlag) {
    case 0:
      return "未定义（UNDEFINED）";
    case 1:
      return "值已变化（VALUE_CHANGED）";
    case 2:
      return "值未变化（VALUE_UNCHANGED）";
    default:
      return `未知（${onChangeFlag}）`;
  }
};

// 导出 CarClient 实例
export const CarClient = new CarClientClass();

// 默认导出 CarClient 实例
export default CarClient;

// CarClient 使用示例
export const carClientExample = async () => {
  try {
    // 订阅车速变化
    await CarClient.subscribeProperty(
      VehiclePropertyNames.VehSpd,
      0,
      (data: CarPropertyValue) => {
        console.log("车速变化:", data.value, "km/h");
      }
    );

    // 订阅方向盘转角变化
    await CarClient.subscribeProperty(
      VehiclePropertyNames.EPS_StrgAng,
      0,
      (data: CarPropertyValue) => {
        console.log("方向盘转角:", data.value, "度");
      }
    );

    // 5秒后取消订阅车速
    setTimeout(async () => {
      await CarClient.unsubscribeProperty(VehiclePropertyNames.VehSpd);
      console.log("已取消车速订阅");
    }, 5000);

    // 10秒后移除所有监听器
    setTimeout(async () => {
      await CarClient.removeAllListeners();
      console.log("已移除所有监听器");
    }, 10000);
  } catch (error) {
    console.error("CarClient 使用示例错误:", error);
  }
};

// test
const carPlugTest = async () => {
  const VehicleProperty = [
    "INFO_VIN",
    "INFO_MAKE",
    "INFO_MODEL",
    "INFO_MODEL_YEAR",
    "INFO_FUEL_CAPACITY",
    "INFO_FUEL_TYPE",
    "INFO_EV_BATTERY_CAPACITY",
    "INFO_EV_CONNECTOR_TYPE",
    "INFO_FUEL_DOOR_LOCATION",
    "INFO_EV_PORT_LOCATION",
    "INFO_DRIVER_SEAT",
    "INFO_EXTERIOR_DIMENSIONS",
    "INFO_MULTI_EV_PORT_LOCATIONS",
    "PERF_ODOMETER",
    "PERF_VEHICLE_SPEED",
    "PERF_VEHICLE_SPEED_DISPLAY",
    "PERF_STEERING_ANGLE",
    "PERF_REAR_STEERING_ANGLE",
    "ENGINE_COOLANT_TEMP",
    "ENGINE_OIL_LEVEL",
    "ENGINE_OIL_TEMP",
    "ENGINE_RPM",
    "WHEEL_TICK",
    "FUEL_LEVEL",
    "FUEL_DOOR_OPEN",
    "EV_BATTERY_LEVEL",
    "EV_CHARGE_PORT_OPEN",
    "EV_CHARGE_PORT_CONNECTED",
    "EV_BATTERY_INSTANTANEOUS_CHARGE_RATE",
    "RANGE_REMAINING",
    "TIRE_PRESSURE",
    "GEAR_SELECTION",
    "CURRENT_GEAR",
    "PARKING_BRAKE_ON",
    "PARKING_BRAKE_AUTO_APPLY",
    "FUEL_LEVEL_LOW",
    "NIGHT_MODE",
    "TURN_SIGNAL_STATE",
    "IGNITION_STATE",
    "ABS_ACTIVE",
    "TRACTION_CONTROL_ACTIVE",
    "HVAC_FAN_SPEED",
    "HVAC_FAN_DIRECTION",
    "HVAC_TEMPERATURE_CURRENT",
    "HVAC_TEMPERATURE_SET",
    "HVAC_DEFROSTER",
    "HVAC_AC_ON",
    "HVAC_MAX_AC_ON",
    "HVAC_MAX_DEFROST_ON",
    "HVAC_RECIRC_ON",
    "HVAC_DUAL_ON",
    "HVAC_AUTO_ON",
    "HVAC_SEAT_TEMPERATURE",
    "HVAC_SIDE_MIRROR_HEAT",
    "HVAC_STEERING_WHEEL_HEAT",
    "HVAC_TEMPERATURE_DISPLAY_UNITS",
    "HVAC_ACTUAL_FAN_SPEED_RPM",
    "HVAC_POWER_ON",
    "HVAC_FAN_DIRECTION_AVAILABLE",
    "HVAC_AUTO_RECIRC_ON",
    "HVAC_SEAT_VENTILATION",
    "HVAC_ELECTRIC_DEFROSTER_ON",
    "DISTANCE_DISPLAY_UNITS",
    "FUEL_VOLUME_DISPLAY_UNITS",
    "TIRE_PRESSURE_DISPLAY_UNITS",
    "EV_BATTERY_DISPLAY_UNITS",
    "FUEL_CONSUMPTION_UNITS_DISTANCE_OVER_VOLUME",
    "VEHICLE_SPEED_DISPLAY_UNITS",
    "ENV_OUTSIDE_TEMPERATURE",
    "AP_POWER_STATE_REQ",
    "AP_POWER_STATE_REPORT",
    "AP_POWER_BOOTUP_REASON",
    "DISPLAY_BRIGHTNESS",
    "HW_KEY_INPUT",
    "HW_ROTARY_INPUT",
    "DOOR_POS",
    "DOOR_MOVE",
    "DOOR_LOCK",
    "MIRROR_Z_POS",
    "MIRROR_Z_MOVE",
    "MIRROR_Y_POS",
    "MIRROR_Y_MOVE",
    "MIRROR_LOCK",
    "MIRROR_FOLD",
    "SEAT_MEMORY_SELECT",
    "SEAT_MEMORY_SET",
    "SEAT_BELT_BUCKLED",
    "SEAT_BELT_HEIGHT_POS",
    "SEAT_BELT_HEIGHT_MOVE",
    "SEAT_FORE_AFT_POS",
    "SEAT_FORE_AFT_MOVE",
    "SEAT_BACKREST_ANGLE_1_POS",
    "SEAT_BACKREST_ANGLE_1_MOVE",
    "SEAT_BACKREST_ANGLE_2_POS",
    "SEAT_BACKREST_ANGLE_2_MOVE",
    "SEAT_HEIGHT_POS",
    "SEAT_HEIGHT_MOVE",
    "SEAT_DEPTH_POS",
    "SEAT_DEPTH_MOVE",
    "SEAT_TILT_POS",
    "SEAT_TILT_MOVE",
    "SEAT_LUMBAR_FORE_AFT_POS",
    "SEAT_LUMBAR_FORE_AFT_MOVE",
    "SEAT_LUMBAR_SIDE_SUPPORT_POS",
    "SEAT_LUMBAR_SIDE_SUPPORT_MOVE",
    "SEAT_HEADREST_HEIGHT_POS",
    "SEAT_HEADREST_HEIGHT_MOVE",
    "SEAT_HEADREST_ANGLE_POS",
    "SEAT_HEADREST_ANGLE_MOVE",
    "SEAT_HEADREST_FORE_AFT_POS",
    "SEAT_HEADREST_FORE_AFT_MOVE",
    "SEAT_OCCUPANCY",
    "WINDOW_POS",
    "WINDOW_MOVE",
    "WINDOW_LOCK",
    "VEHICLE_MAP_SERVICE",
    "OBD2_LIVE_FRAME",
    "OBD2_FREEZE_FRAME",
    "OBD2_FREEZE_FRAME_INFO",
    "OBD2_FREEZE_FRAME_CLEAR",
    "HEADLIGHTS_STATE",
    "HIGH_BEAM_LIGHTS_STATE",
    "FOG_LIGHTS_STATE",
    "HAZARD_LIGHTS_STATE",
    "HEADLIGHTS_SWITCH",
    "HIGH_BEAM_LIGHTS_SWITCH",
    "FOG_LIGHTS_SWITCH",
    "HAZARD_LIGHTS_SWITCH",
    "CABIN_LIGHTS_STATE",
    "CABIN_LIGHTS_SWITCH",
    "READING_LIGHTS_STATE",
    "READING_LIGHTS_SWITCH",
    "SUPPORT_CUSTOMIZE_VENDOR_PERMISSION",
    "DISABLED_OPTIONAL_FEATURES",
    /*     
    //不支持的
    // "INITIAL_USER_INFO",
    // "SWITCH_USER",
    // "CREATE_USER",
    // "REMOVE_USER",
    // "USER_IDENTIFICATION_ASSOCIATION", 
    */
    "EVS_SERVICE_REQUEST",
    "POWER_POLICY_REQ",
    "POWER_POLICY_GROUP_REQ",
    "CURRENT_POWER_POLICY",
    "WATCHDOG_ALIVE",
    "WATCHDOG_TERMINATED_PROCESS",
    "VHAL_HEARTBEAT",
    "CLUSTER_SWITCH_UI",
    "CLUSTER_DISPLAY_STATE",
    "CLUSTER_REPORT_STATE",
    "CLUSTER_REQUEST_DISPLAY",
    "CLUSTER_NAVIGATION_STATE",
    "ELECTRONIC_TOLL_COLLECTION_CARD_TYPE",
    "ELECTRONIC_TOLL_COLLECTION_CARD_STATUS",
    "HW_CUSTOM_INPUT",
    "CRITICALLY_LOW_TIRE_PRESSURE",
    "HVAC_TEMPERATURE_VALUE_SUGGESTION",
    "EPOCH_TIME",
    "STORAGE_ENCRYPTION_BINDING_SEED",
  ];
  const soa_types = ["BuckleAdjust_SRV_SecLChdSeatPosnSwReq"];
  const can_types = ["RMotTemp"];
  // try {
  //   const property = await Car.getProperties({
  //     propertyNames: [...VehicleProperty, ...soa_types, ...can_types],
  //     areaId: 0,
  //   });
  //   console.log("[TEST] 测试 CarPlugin getProperties", property);
  // } catch (error) {
  //   console.log("[TEST] 测试 CarPlugin getProperties error", error);
  // }
  // try {
  //   const info = await Car.getVehicleInfos({
  //     infoIds: Object.values(VehicleInfoIds),
  //   });
  //   console.log("[TEST] 测试 CarPlugin getVehicleInfos", info);
  // } catch (error) {
  //   console.log("[TEST] 测试 CarPlugin getVehicleInfos error", error);
  // }
};

setTimeout(() => {
  if (isNative) {
    // carPlugTest();
  }
}, 5000);
